import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { UserProfile } from '@app/_models';
import { RequestAccessList } from '@app/_models/device-users';
import { Property } from '@app/_models/propery.model';
import { AuthResponse } from '@app/_models/responses';
import { UserService, AuthenticationService } from '@app/_services';
import { AccessManagementService } from '@app/_services/access-management.service';
import { PropertyService } from '@app/_services/property.service';
import { environment } from '@environments/environment';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { faPaperPlane, faBell, faEnvelope, faEyeSlash, faMapMarkedAlt, faPhoneAlt, faMapPin, faMapSigns, faEye, faUser, faBan, faQuestionCircle, faCheckCircle, faTimesCircle, faSearch, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { ToastrService } from 'ngx-toastr';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-access-management',
    templateUrl: './access-management.component.html',
    styleUrls: ['./access-management.component.css'],
    standalone: false
})
export class AccessManagementComponent implements OnInit {

  faPaperPlane = faPaperPlane;
  faEnvelope = faEnvelope;
  faEyeSlash = faEyeSlash;
  faMapMarkedAlt = faMapMarkedAlt;
  faPhoneAlt = faPhoneAlt;
  faMapPin = faMapPin;
  faMapSigns = faMapSigns;
  faEye = faEye;
  faUser = faUser;
  faBan = faBan;
  faBell = faBell;
  faQuestionCircle = faQuestionCircle;
  faCheckCircle = faCheckCircle;
  faTimesCircle = faTimesCircle;
  faSearch = faSearch;
  faSpinner = faSpinner;
  faWhatsapp = faWhatsapp;

  searchUser = signal('');
  loadProfile = signal<Record<number, boolean>>({});
  p = signal(1);
  help = signal(false);
  current_user = signal<AuthResponse | null>(null);
  reqlist = signal<RequestAccessList[]>([]);
  proplist = signal<Property[]>([]);
  userprofile = signal<UserProfile | null>(null);
  property_id = signal<string | number | null>(null);

  private destroyRef = inject(DestroyRef);

  constructor(private userService: UserService,
    private accessManagmentService: AccessManagementService,
    private propertyService: PropertyService,
    private route: ActivatedRoute,
    private router: Router,
    private authenticationService: AuthenticationService,
    private toastr: ToastrService) { }

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (params) => {
          const propertyId = params.get('propertyid');
          this.property_id.set(propertyId);
          if (propertyId) {
            this.loadRequestAccessList(propertyId);
          }
        }
      });

    this.authenticationService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (user) => {
          this.current_user.set(user || null);
        }
      });
  }

  private loadRequestAccessList(propertyId: string) {
    const numPropertyId = propertyId ? Number(propertyId) : propertyId;
    this.userService.getRequestAccessList(numPropertyId as any)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          if (!environment.production) {
            console.log('Request access list: ', data);
          }
          this.reqlist.set(data);
        },
        error: (error) => {
          console.log('Request access error', error);
        }
      });
  }
  openDiv(requestid: number, username: string) {
    const currentReqList = this.reqlist();
    if (currentReqList.find(r => r.id == requestid)?.opened) {
      const updated = currentReqList.map(r =>
        r.id === requestid ? { ...r, opened: false } : r
      );
      this.reqlist.set(updated);
    } else {
      const loadDetails = { ...this.loadProfile() };
      loadDetails[requestid] = true;
      this.loadProfile.set(loadDetails);

      this.userService.getUserProfile(username)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            this.userprofile.set(data);
            if (!environment.production) {
              console.log('Profile: ', data);
            }

            const updated = currentReqList.map(r =>
              r.id === requestid ? { ...r, opened: true } : r
            );
            this.reqlist.set(updated);

            loadDetails[requestid] = false;
            this.loadProfile.set(loadDetails);
          },
          error: (error) => {
            this.toastr.error('Failed to retrieve user profile.', 'Error');
            loadDetails[requestid] = false;
            this.loadProfile.set(loadDetails);
          }
        });
    }
  }

  sendWhatsappUser(username: string) {
    this.userService.getUserProfile(username)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.userprofile.set(data);
          if (!environment.production) {
            console.log('Profile: ', data);
          }
          location.href = "https://wa.me/+1" + data.phone + "/?text=";
        },
        error: (error) => {
          this.toastr.error('Failed to retrieve user profile.', 'Error');
        }
      });
  }

  toggleHelp() {
    this.help.set(!this.help());
  }

  removeAccess(username: string, propid: number) {
    if (!environment.production) {
      console.log('User value: ', this.current_user()?.username);
    }
    const request = { username: username, approved_by: this.current_user()?.username, property_id: propid };
    this.accessManagmentService.denyAccess(request);
    this.reloadCurrentRoute();
  }

  deleteAccess(id: number) {
    this.accessManagmentService.deleteAccess(id);
    this.reloadCurrentRoute();
  }

  reloadCurrentRoute() {
    let currentUrl = this.router.url;
    this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
        this.router.navigate([currentUrl]);
    });
  }

  approveAccess(username: string, propid: number) {
    const request = { username: username, approved_by: this.current_user()?.username, property_id: propid };
    this.accessManagmentService.grantAccess(request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.toastr.success(data.message, 'Success');
          this.reloadCurrentRoute();
        },
        error: (error) => {
          this.toastr.error(error.message, 'Error');
        }
      });
  }

}
